import { z } from 'zod';
import {
  defaultRowsPerPage,
  defaultPage,
  bankAccountTypes,
  LocationTypes,
} from '../Utlils';
import {
  containsDynamicVariable,
  isValidObjectId,
  redirectDynamicVariables,
  validateURLStructure,
} from './helperFunctions';

export const optionalMinMaxLength = (min, max, fieldName) =>
  z
    .string()
    .trim()
    .refine(
      (val) =>
        val === '' ||
        (val.length >= min && (max !== undefined ? val.length <= max : true)),
      (val) => ({
        message:
          max !== undefined
            ? `${fieldName} must be between ${min} and ${max} characters.`
            : `${fieldName} must be at least ${min} characters.`,
      }),
    )
    .optional();

export const zodMetadataSchema = z.record(z.any()).optional();

export const zodStringOptionSchema = z.object(
  {
    label: z
      .string({
        invalid_type_error: 'Expected a string for label.',
        required_error: 'Label is required.',
      })
      .trim()
      .min(1, { message: 'Label cannot be empty.' }),
    value: z
      .string({
        invalid_type_error: 'Expected a string for value.',
        required_error: 'Value is required',
      })
      .trim()
      .min(1, { message: 'Value cannot be empty.' }),
  },
  {
    invalid_type_error: 'Option must be an object',
    required_error: 'Option is required',
  },
);

export const zodCountriesSchema = z.object(
  {
    label: z
      .string({
        invalid_type_error: 'Expected a string for label.',
        required_error: 'Label is required.',
      })
      .trim()
      .min(1, { message: 'Label cannot be empty.' }),
    value: z
      .string({
        invalid_type_error: 'Expected a string for value.',
        required_error: 'Value is required',
      })
      .trim()
      .min(1, { message: 'Value cannot be empty.' }),
    icon: z
      .string({
        invalid_type_error: 'Expected a string for icon.',
        required_error: 'icon is required.',
      })
      .trim()
      .min(1, { message: 'icon cannot be empty.' }),
  },
  {
    invalid_type_error: 'Country must be an object',
    required_error: 'Country is required',
  },
);

export const zodNumberOptionSchema = z.object(
  {
    label: z
      .string({
        invalid_type_error: 'Expected a string for label.',
        required_error: 'Label is required.',
      })
      .trim()
      .min(1, { message: 'Label cannot be empty!' }),
    value: z
      .number({
        invalid_type_error: 'Value must be a number',
        required_error: 'Value is required',
      })
      .nonnegative({ message: 'Value cannot be negative' }),
  },
  {
    invalid_type_error: 'Option must be an object',
    required_error: 'Option is required',
  },
);

export const zodCurrencySchema = z.object(
  {
    label: z
      .string({
        required_error: 'Currency label is required.',
        invalid_type_error: 'Currency label must be a valid string.',
      })
      .trim()
      .min(1, { message: 'Currency label is required.' }),
    value: z
      .string({
        required_error: 'Currency value is required.',
        invalid_type_error: 'Currency value must be a valid string.',
      })
      .trim()
      .min(1, { message: 'Currency value is required.' }),
    symbol: z
      .string({
        required_error: 'Currency symbol is required.',
        invalid_type_error: 'Currency symbol must be a valid string.',
      })
      .trim()
      .min(1, { message: 'Currency symbol is required.' }),
  },
  {
    required_error: 'Currency is required!',
    invalid_type_error: 'Currency must be of type Object!',
  },
);

export const zodPhoneSchema = z.object(
  {
    countryCode: z
      .string({
        required_error: 'Country code is required.',
        invalid_type_error: 'Expected a string for Country Code.',
      })
      .trim()
      .min(1, { message: 'Country code cannot be empty' }),
    number: z
      .string({
        required_error: 'Phone number is required.',
        invalid_type_error: 'Expected a string for Phone number.',
      })
      .trim()
      .min(5, { message: 'Phone number must be at least 5 digits' })
      .max(20, { message: 'Phone number too long' }),
  },
  {
    invalid_type_error: 'Phone must be an object',
    required_error: 'Phone is required',
  },
);

export const zodValidatePassword = z
  .string({ required_error: 'Password is required' })
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must not exceed 128 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(
    /[^A-Za-z0-9]/,
    'Password must contain at least one special character',
  );

export const zodValidateEmail = z
  .string({
    required_error: 'Email is required',
    invalid_type_error: 'Email must be a string',
  })
  .email({ message: 'Email must be a valid email' });

export const zodRowsPageSchema = z.object({
  rows: z
    .number({
      required_error: 'Rows is required',
      invalid_type_error: 'Rows must be a number',
    })
    .min(1, { message: 'Rows must be a greater than 0' })
    .nonnegative({ message: 'Rows cannot be negative' })
    .default(defaultRowsPerPage),
  page: z
    .number({
      required_error: 'Page is required',
      invalid_type_error: 'Page must be a number',
    })
    .min(1, { message: 'Page must be a greater than 0' })
    .nonnegative({ message: 'Page cannot be negative' })
    .default(defaultPage),
  reset: z
    .boolean({ invalid_type_error: 'Reset must be a boolean' })
    .default(false),
  search: z
    .string({ invalid_type_error: 'Search text must be type of string' })
    .trim()
    .toLowerCase()
    .optional(),
  startDate: z
    .string({ invalid_type_error: 'Start Date must be type of string' })
    .optional(),
  endDate: z
    .string({
      invalid_type_error: 'End Date must be type of string',
    })
    .optional(),
  status: z
    .string({ invalid_type_error: 'Status type must be string' })
    .toLowerCase()
    .optional(),
  tab: z
    .string({ invalid_type_error: 'Tab type must be string' })
    .optional()
    .default('all'),
  suppliers: z
    .array(
      z
        .string({ invalid_type_error: 'Supplier ID must be a string' })
        .refine(isValidObjectId, 'Supplier ID must be a valid object ID')
        .optional(),
      { invalid_type_error: 'Suppliers must be an array' },
    )
    .default([]),
  surveys: z
    .array(
      z
        .string({ invalid_type_error: 'Survey ID must be a string' })
        .refine(isValidObjectId, 'Survey ID must be a valid object ID')
        .optional(),
      { invalid_type_error: 'Surveys must be an array' },
    )
    .default([]),
});

export const zodAddressSchema = z.object(
  {
    street1: z
      .string({
        required_error: 'Street Address is required.',
        invalid_type_error: 'Expected a string for Street Address.',
      })
      .trim()
      .min(1, { message: 'Street Address cannot be empty.' })
      .min(5, {
        message: 'Street Address must be at least 5 characters long.',
      }),
    street2: optionalMinMaxLength(1, 500, 'Street Address 2'),
    country: zodStringOptionSchema,
    state: zodStringOptionSchema,
    city: z
      .string({
        required_error: 'City is required.',
        invalid_type_error: 'Expected a string for City.',
      })
      .trim()
      .min(1, { message: 'City cannot be empty.' })
      .min(3, {
        message: 'City must be at least 3 characters long.',
      }),
    postalCode: z
      .string({
        required_error: 'Postal code is required',
        invalid_type_error: 'Expected a string for Postal Code.',
      })
      .trim()
      .min(1, { message: 'Postal code cannot be empty.' })
      .min(4, { message: 'Postal Code must be at least 4 characters long.' }),
  },
  {
    invalid_type_error: 'Address must be an object',
    required_error: 'Address is required',
  },
);

export const zodLocationSchema = z.object(
  {
    address: zodAddressSchema,
    locationType: z.enum(LocationTypes, {
      required_error: 'Location type is required',
      invalid_type_error: 'Invalid location type',
    }),
    taxIds: z
      .array(zodStringOptionSchema, {
        required_error: 'Tax IDs are required',
        invalid_type_error: 'Tax IDs must be an array of objects',
      })
      .optional(),
    invoicePrefix: optionalMinMaxLength(1, 10, 'Invoice Prefix'),
  },
  {
    invalid_type_error: 'Location must be an object',
    required_error: 'Location is required',
  },
);

export const zodBankAccountSchema = z.object(
  {
    _id: z
      .string()
      .refine(isValidObjectId, {
        message: 'Invalid ObjectId for bank account.',
      })
      .optional(),
    accountHolderName: z
      .string({
        required_error: 'Account holder name is required.',
        invalid_type_error: 'Expected a string for Account Holder Name.',
      })
      .trim()
      .min(1, { message: 'Account Holder Name cannot be empty.' })
      .min(5, {
        message: 'Account Holder Name must be at least 5 characters long.',
      }),
    bankName: z
      .string({
        required_error: 'Bank name is required.',
        invalid_type_error: 'Expected a string for Bank Name.',
      })
      .trim()
      .min(1, { message: 'Bank name Name cannot be empty.' })
      .min(2, {
        message: 'Bank name must be at least 2 characters long.',
      }),
    accountNumber: z
      .string({
        required_error: 'Account number is required.',
        invalid_type_error: 'Expected a string for Account Number.',
      })
      .trim()
      .min(1, { message: 'Account Number cannot be empty.' })
      .min(8, {
        message: 'Account Number must be at least 8 characters long.',
      }),
    ifscCode: optionalMinMaxLength(1, 11, 'IFSC Code'),
    bankType: z
      .enum(bankAccountTypes, {
        required_error: 'Bank account type is required.',
        invalid_type_error: 'Invalid bank account type Selected.',
      })
      .default(bankAccountTypes[0]),
    ibanNumber: optionalMinMaxLength(15, 34, 'IBAN Number'),
    swiftBic: optionalMinMaxLength(8, 11, 'Swift/BIC code'),
    nationalBankCode: optionalMinMaxLength(3, 15, 'National Bank'),
    bankAddress: zodAddressSchema,
    routingNumber: optionalMinMaxLength(1, 9, 'Routing Number'),
  },
  {
    invalid_type_error: 'Bank account must be an object',
    required_error: 'Bank account is required',
  },
);

export const urlWithDynamicVar = (fieldName) =>
  z
    .string({
      invalid_type_error: `Expected string for ${fieldName}`,
      required_error: `${fieldName} URL is required.`,
    })
    .trim()
    .url({ message: `Invalid ${fieldName} URL` })
    .refine(containsDynamicVariable, {
      message: `Must contain at least one dynamic variable: ${redirectDynamicVariables.join(', ')}`,
    });

export const zodRedirectSchema = z.object(
  {
    complete: z
      .string({
        required_error: 'complete link is required !',
        invalid_type_error: 'complete link must be a string',
      })
      .trim()
      .url({ message: 'Invalid complete link !' })
      .transform((url, ctx) => {
        const error = validateURLStructure(url);
        if (error) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error,
          });
        }
        return url;
      }),
    terminate: z
      .string({
        required_error: 'terminate link is required !',
        invalid_type_error: 'terminate link must be a string',
      })
      .trim()
      .url({ message: 'Invalid terminate link !' })
      .transform((url, ctx) => {
        const error = validateURLStructure(url);
        if (error) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error,
          });
        }
        return url;
      }),
    securityTerminate: z
      .string({
        required_error: 'security terminate link is required !',
        invalid_type_error: 'security terminate link must be a string',
      })
      .trim()
      .url({ message: 'Invalid security terminate link !' })
      .transform((url, ctx) => {
        const error = validateURLStructure(url);
        if (error) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error,
          });
        }
        return url;
      }),
    quotaFull: z
      .string({
        required_error: 'quota full link is required !',
        invalid_type_error: 'quota full link must be a string',
      })
      .trim()
      .url({ message: 'Invalid quota full link !' })
      .transform((url, ctx) => {
        const error = validateURLStructure(url);
        if (error) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error,
          });
        }
        return url;
      }),
  },
  {
    invalid_type_error: 'Redirects must be an object',
    required_error: 'Redirects are required',
  },
);

export const idValidator = z.object({
  id: z
    .string({
      required_error: 'Team member ID is required.',
      invalid_type_error: 'Expected a string for team member ID.',
    })
    .refine(isValidObjectId, {
      message: 'Invalid ObjectId for team member.',
    }),
});

export const zodObjectIdSchema = z.string().refine(isValidObjectId, {
  message: 'Invalid ObjectId',
});
